import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import '../styles/Hero.css';

const Hero = ({ translations, language }) => {
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    let ticking = false;
    const handleMouseMove = (e) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setMousePosition({
            x: (e.clientX / window.innerWidth) * 100,
            y: (e.clientY / window.innerHeight) * 100,
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <section id="home" className="hero-section" ref={ref}>
      <div 
        className="hero-bg-gradient"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(139, 92, 246, 0.3) 0%, transparent 50%)`
        }}
      ></div>
      
      <div className="hero-particles">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          ></div>
        ))}
      </div>

      <div className="container">
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <div className="hero-left">
            <motion.h1 
              className="hero-title"
              variants={itemVariants}
            >
              <span className="gradient-text neon-text">{translations.hero.title}</span>
            </motion.h1>
            
            <motion.h2 
              className="hero-subtitle"
              variants={itemVariants}
            >
              {translations.hero.subtitle}
            </motion.h2>
            
            <motion.p 
              className="hero-description"
              variants={itemVariants}
            >
              {translations.hero.description}
            </motion.p>
            
            <motion.div 
              className="hero-buttons"
              variants={itemVariants}
            >
              <motion.button
                className="btn-futuristic primary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {translations.hero.cta1}
              </motion.button>
              
              <motion.button
                className="btn-futuristic secondary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {translations.hero.cta2}
              </motion.button>
            </motion.div>
          </div>

          <div className="hero-right">
            <motion.div
              className="hero-visual"
              variants={itemVariants}
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="holographic-chart">
                <div className="chart-bars">
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="chart-bar"
                      initial={{ height: 0 }}
                      animate={{ height: `${20 + Math.random() * 60}%` }}
                      transition={{
                        duration: 2,
                        delay: i * 0.2,
                        repeat: Infinity,
                        repeatType: "reverse",
                        repeatDelay: 1,
                      }}
                    ></motion.div>
                  ))}
                </div>
                <div className="chart-glow"></div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          className="hero-stats"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="stat-item" variants={itemVariants}>
            <div className="stat-number">
              {inView && <CountUp end={150} duration={2} suffix="+" />}
            </div>
            <div className="stat-label">{translations.hero.stats.clients}</div>
          </motion.div>
          
          <motion.div className="stat-item" variants={itemVariants}>
            <div className="stat-number">
              {inView && <CountUp end={300} duration={2} suffix="%" />}
            </div>
            <div className="stat-label">{translations.hero.stats.roi}</div>
          </motion.div>
          
          <motion.div className="stat-item" variants={itemVariants}>
            <div className="stat-number">
              {inView && <CountUp end={500} duration={2} suffix="+" />}
            </div>
            <div className="stat-label">{translations.hero.stats.campaigns}</div>
          </motion.div>
        </motion.div>
      </div>

      <div className="scroll-indicator-hero">
        <motion.div
          className="scroll-arrow"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <i className="fas fa-chevron-down"></i>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;